import { ArrowRight, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AirlineLogo } from '../shared/AirlineLogo';
import { CouponStatusBadge } from '../shared/StatusBadge';
import { formatDateTime } from '../utils/formatters';
import type { FareInfo } from '../utils/types';

interface FareInfoCardProps {
  fareInfo: FareInfo;
  className?: string;
}

export function FareInfoCard({ fareInfo, className }: FareInfoCardProps) {
  if (!fareInfo.flight) return null;

  return (
    <div
      className={cn(
        'rounded-lg border border-slate-100 bg-gradient-to-r from-slate-50 to-white p-3',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AirlineLogo airlineCode={fareInfo.flight.marketingAirlineCode} size="sm" />
          <div>
            <span className="font-semibold text-slate-800">
              {fareInfo.flight.marketingAirlineCode}{fareInfo.flight.marketingFlightNumber}
            </span>
            <span className="mx-2 text-slate-400">•</span>
            <span className="text-sm text-slate-600">
              {fareInfo.flight.departure?.locationCode}
              <ArrowRight className="inline h-3 w-3 mx-1 text-slate-400" />
              {fareInfo.flight.arrival?.locationCode}
            </span>
          </div>
        </div>
        <CouponStatusBadge status={fareInfo.couponStatus} />
      </div>

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Calendar className="h-3 w-3" />
          {fareInfo.flight.departure?.dateTime && (
            <span>{formatDateTime(fareInfo.flight.departure.dateTime)}</span>
          )}
        </div>
        <div className="text-sm">
          <span className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
            {fareInfo.fareClass}
          </span>
          {fareInfo.fareFamilyCode && (
            <span className="ml-1 text-xs text-slate-400">
              ({fareInfo.fareFamilyCode})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface OriginalDocumentCardProps {
  documentNumber: string;
  issueDate: string;
  className?: string;
}

export function OriginalDocumentCard({ documentNumber, issueDate, className }: OriginalDocumentCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border-l-2 border-slate-300 bg-slate-50 p-3',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <span className="text-slate-500">Original ticket:</span>{' '}
          <span className="font-mono text-slate-700">{documentNumber}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Calendar className="h-3 w-3" />
          <span>Issued {new Date(issueDate).toLocaleDateString('de-DE')}</span>
        </div>
      </div>
    </div>
  );
}
